import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { AddMissionLinkedLayersBody } from "@nova/schemas"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import useSWR from "swr"
import useSWRMutation from "swr/mutation"

const fetchLinkedLayers = (orgId: string, missionId: string) => {
    return fetchApiGateway("get /org/:orgId/fleet/mission/:missionId/linked-layers", {
        params: { orgId, missionId },
    })
}

export const useLinkedLayers = (missionId: string) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, isLoading } = useSWR(orgId ? [orgId, missionId, "linked-layers"] : null, () =>
        fetchLinkedLayers(orgId!, missionId)
    )

    return { data, isLoading }
}

const fetchProjectLayers = (orgId: string, projectId: string) => {
    return fetchApiGateway("get /org/:orgId/project/:projectId/layers", {
        params: { orgId, projectId },
    })
}

export const useProjectLayers = (projectId: string) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, isLoading } = useSWR(
        orgId && projectId ? [orgId, projectId, "project-layers"] : null,
        () => fetchProjectLayers(orgId!, projectId)
    )

    return { data, isLoading }
}

export const postLinkedLayers = (
    orgId: string,
    missionId: string,
    body: AddMissionLinkedLayersBody
) => {
    return fetchApiGateway("post /org/:orgId/fleet/mission/:missionId/linked-layers", {
        body,
        params: { orgId, missionId },
    })
}

export const useRemoveLinkedLayer = (missionId: string) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const { mutate } = useSWR(orgId ? [orgId, missionId, "linked-layers"] : null)

    const { trigger, isMutating } = useSWRMutation(
        [orgId, missionId, "remove-linked-layer"],
        (_key, req: { arg: string }) =>
            orgId
                ? fetchApiGateway(
                      "delete /org/:orgId/fleet/mission/:missionId/linked-layers/:layerId",
                      { params: { orgId, missionId, layerId: req.arg } }
                  )
                : undefined,
        { onSuccess: () => mutate() }
    )

    return { removeLinkedLayer: trigger, isMutating }
}

export const useAddLinkedLayers = (missionId: string) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const { mutate } = useSWR(orgId ? [orgId, missionId, "linked-layers"] : null)

    const { trigger, isMutating } = useSWRMutation(
        [orgId, missionId, "add-linked-layers"],
        (_key, req: { arg: AddMissionLinkedLayersBody }) =>
            orgId ? postLinkedLayers(orgId, missionId, req.arg) : undefined,
        { onSuccess: () => mutate() }
    )

    return { addLinkedLayers: trigger, isMutating }
}
